import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ListingCarCard } from "@/components/car-listing/listing-car-card";
import type { ListingCarCardData } from "@/components/car-listing/listing-car-types";

const GRID_CLASS =
  "grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3";

type Props = {
  cars: ListingCarCardData[] | undefined;
  isLoading: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  favoriteCarIds: Set<number>;
  onToggleFavorite: (carId: number) => void;
  /** Visual variant for cards (matches listings page themes). */
  variant?: "default" | "fixed";
  /** Extra content under empty-state message (e.g. clear filters). */
  emptyExtra?: ReactNode;
  /** Show compare button on each card (listings page). */
  enableCompare?: boolean;
  /** صفحة المزادات · ألوان تحميل وبطاقات متوافقة مع الهوية */
  brandAccent?: boolean;
};

function GridSkeleton({ brandAccent = false }: { brandAccent?: boolean }) {
  return (
    <div className={GRID_CLASS} aria-busy="true" aria-label="جاري تحميل السيارات">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="flex min-h-[22rem] flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white p-3 shadow-sm"
        >
          <div className="aspect-[4/3] animate-pulse rounded-2xl bg-neutral-100" />
          <div className="space-y-3 px-1 pt-3">
            <div className="h-5 w-4/5 animate-pulse rounded bg-neutral-100" />
            <div className="h-3 w-16 animate-pulse rounded bg-neutral-100" />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Array.from({ length: 5 }).map((__, i) => (
                <div key={i} className="h-4 animate-pulse rounded bg-neutral-100" />
              ))}
            </div>
            <div className="flex items-end justify-between pt-2">
              <div className="h-8 w-24 animate-pulse rounded bg-neutral-100" />
              <div className="h-10 w-28 animate-pulse rounded-full bg-neutral-100" />
            </div>
          </div>
        </div>
      ))}
      <div className="col-span-full flex justify-center pt-2">
        <Loader2
          className="size-6 animate-spin text-accent"
          aria-hidden
        />
      </div>
    </div>
  );
}

export function ListingCarGrid({
  cars,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  favoriteCarIds,
  onToggleFavorite,
  variant = "default",
  emptyExtra,
  brandAccent = false,
  enableCompare = false,
}: Props) {
  if (isLoading && cars === undefined) {
    return <GridSkeleton brandAccent={brandAccent} />;
  }

  if (isError) {
    return (
      <div className="space-y-4 py-12 text-center">
        <h3 className="text-lg font-medium text-destructive">تعذر تحميل القائمة</h3>
        <p className="text-sm text-muted-foreground">{errorMessage ?? "حدث خطأ"}</p>
        {onRetry ? (
          <Button type="button" variant="outline" onClick={onRetry}>
            إعادة المحاولة
          </Button>
        ) : null}
      </div>
    );
  }

  if (!cars?.length) {
    return (
      <div className="py-12 text-center">
        <h3 className="text-xl font-medium text-muted-foreground">لا توجد سيارات مطابقة لمعاييرك</h3>
        <p className="mt-2 text-muted-foreground">جرّب تعديل الفلاتر أو البحث</p>
        {emptyExtra ? <div className="mt-4">{emptyExtra}</div> : null}
      </div>
    );
  }

  return (
    <div className={GRID_CLASS}>
      {cars.map((car, index) => (
        <ListingCarCard
          key={car.id}
          car={car}
          isFavorite={favoriteCarIds.has(car.id)}
          onToggleFavorite={onToggleFavorite}
          variant={variant}
          brandAccent={brandAccent}
          enableCompare={enableCompare}
          imagePriority={index < 6}
        />
      ))}
    </div>
  );
}
